import * as echarts from "echarts";
import { default as EChartsReact, default as ReactEcharts } from "echarts-for-react";
import { t } from "i18next";
import { DateTime, Interval } from "luxon";
import { useEffect, useState } from "react";
import ReactDomServer from "react-dom/server";
import { useAppStore } from "../../stores/RootStore";
import { convertTimezone } from "../../lib/utils";
import { hexColor, hslColor, rgbColor } from "../../enums/colors";
import { DateRange } from "react-day-picker";
import Spinner from "../Spinner/Spinner";


interface BaseChartProps {
  type: string,
  title: string,
  data: ChartData[], 
  selectedDate: DateRange,
  onChangeDates: Function;
  loading: boolean;
}

interface ChartData {
    at: number,
    personnel_id: any,
    rfid: string,
    v: "in" | "out" | null
}

const EBaseEventChart = ({ data, selectedDate, type, title, onChangeDates, loading }:BaseChartProps) => {
  const store = useAppStore()
  const [ref, setRef] = useState<EChartsReact | null>();
  const [loaded, setLoaded] = useState(false)
  const [currentInterval, setCurrentInterval] = useState<number>(3600)
  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    let cd: any[] = []
    let startDate = (data[0].at + convertTimezone(data[0].at))/1000

    data.map((e, idx) => {
      if (e.v !== undefined) {
        if(e.v == "in") {
          let ae = data[idx + 1] as ChartData
          let start = (e.at + convertTimezone(e.at))/1000
          let finish: number
          if(DateTime.local() > DateTime.fromJSDate(selectedDate.to!)) {
            finish = (ae.at + convertTimezone(ae.at))/1000
          } else {
            finish = DateTime.local().toSeconds()
          }
          let duration = finish - start
          cd.push({
            name: e.rfid,
            value: [
              0,
              start - startDate,
              start - startDate + duration,
              duration,
              start,
              finish
            ],
            itemStyle: { color: "#16A34A" },
          })
        } else {
          let be = data[idx - 1] as ChartData
          let start = (be.at + convertTimezone(be.at))/1000
          let finish = (e.at + convertTimezone(e.at))/1000
          let duration = finish - start
          if(be.v == "in") {
            return
          } else {
            cd.push({
              name: e.rfid,
              value: [
                0,
                start - startDate,
                start - startDate + duration,
                duration,
                start,
                finish
              ],
              itemStyle: { color: "#16A34A"},
            })
          } 
        }
      }
    })
    setCurrentInterval(Math.round(DateTime.fromJSDate(selectedDate.to!).diff(DateTime.fromJSDate(selectedDate.from!), ['days']).days) * 3600)
    setChartData(cd)
    setLoaded(true)
  }, [data])

  function renderItem(
    params: { coordSys: { x: any; y: any; width: any; height: any } },
    api: {
      value: (arg0: number) => any;
      coord: (arg0: any[]) => any;
      size: (arg0: number[]) => number[];
      style: () => any;
    }
  ) {
    let dayIndex = api.value(0);

    let start = api.coord([api.value(1), dayIndex]);
    let end = api.coord([api.value(2), dayIndex]);
    let height = api.size([0, 1])[1] * 0.8;

    let rectShape = echarts.graphic.clipRectByRect(
      {
        x: start[0],
        y: start[1] - height,
        width: end[0] - start[0],
        height: height,
      },
      {
        x: params.coordSys.x,
        y: params.coordSys.y,
        width: params.coordSys.width,
        height: params.coordSys.height,
      }
    );

    return (
      rectShape && {
        type: "rect",
        transition: ["shape"],
        shape: rectShape,
        style: api.style(),
      }
    );
  }

  const getOptions = () => ({
    animation: false,
    title: {
      text: title,
      textStyle: {
        fontSize: 14,
        lineHeight: 16,
        fontWeight: 'bold',
        color: store.theme === "dark" ? hexColor.border : hexColor.borderForeground,
      },
      height: 60,
      padding: 0,
      textVerticalAlign: 'auto'
    },
    tooltip: {
      // formatter: function (params: { marker: any; name: any; value: (number | EquipmentInteractionData | undefined)[] }) {
      //   return ReactDomServer.renderToStaticMarkup(<TooltipHeatmap params={params} />);
      // },
    },
    grid: {
      left: "0%",
      right: "0%",
      bottom: "0%",
      top: "50px",
      containLabel: false,
      borderColor: store.theme === "dark" ? hexColor.border : hexColor.borderForeground,
      height: 20,
    },
    xAxis: {
      position: "top",
      axisLine: {
        show: false
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: store.theme === "dark" ? hexColor.borderForeground : hexColor.border
        }
      },
      min: 0,
      max: DateTime.fromJSDate(selectedDate.to!).toSeconds() - DateTime.fromJSDate(selectedDate.from!).toSeconds(),
      interval: currentInterval,
      scale: true,
      axisLabel: {
        color: store.theme === "dark" ? hexColor.border : hexColor.borderForeground,
        showMinLabel: false,
        showMaxLabel: false,
        alignMinLabel: 'left',
        alignMaxLabel: 'right', 
        hideOverlap: true,
        formatter: function (val: number) {
          return DateTime.local().startOf("day").plus({ seconds: val }).toFormat("HH:mm");
        },
      },
    },
    yAxis: [
      {
        show: false,
        axisLabel: {},
      },
    ],
    series: [
      {
        type: "custom",
        renderItem: renderItem,
        itemStyle: {
          opacity: 0.8,
        },
        encode: {
          x: [1, 2],
          y: 0,
        },
        data: chartData,
      }
    ],
  });

  return (
    loading ? <div className="h-10 w-full"> <Spinner /></div> : <div className="flex flex-col space-y-2">
        <ReactEcharts
          option={getOptions()}
          showLoading={!loaded}
          loadingOption={{
            text: '',
            color: hslColor.primary,
            // textColor: store.theme === "dark" ? hslColor.lightBorder : hslColor.darkBorder,
            maskColor: store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
            zlevel: 0,
            // Font size. Available since `v4.8.0`.
            fontSize: 12,
            // Show an animated "spinner" or not. Available since `v4.8.0`.
            showSpinner: true,
            // Radius of the "spinner". Available since `v4.8.0`.
            spinnerRadius: 8,
            // Line width of the "spinner". Available since `v4.8.0`.
            lineWidth: 2,
            // Font thick weight. Available since `v5.0.1`.
            fontWeight: 'normal',
            // Font style. Available since `v5.0.1`.
            fontStyle: 'normal',
            // Font family. Available since `v5.0.1`.
            fontFamily: 'sans-serif'
          }}
          ref={(e) => {
            setRef(e);
          }}
          style={{ width: "100%", height: 80 }}
          opts={{ renderer: "canvas" }}
        />
      </div>
  );
};

// const TooltipHeatmap = ({ params }: { params: { name: any; value: (number | EquipmentInteractionData | undefined)[] } }) => {
//   return (
//     <div className="flex flex-col w-[16rem]">
//       <div className="capitalize">{t(`${params.name}`)}</div>
//       <div className="capitalize">{DateTime.fromSeconds(Number(params.value[4])).toFormat("HH:mm:ss")}</div>
//       <div className="capitalize">{DateTime.fromSeconds(Number(params.value[5])).toFormat("HH:mm:ss")}</div>
//       <div className="capitalize">
//         {(params.value[9] as EquipmentInteractionData)?.personnel?.last_name}{" "}
//         {(params.value[9] as EquipmentInteractionData)?.personnel?.first_name}
//       </div>
//     </div>
//   );
// };

export default EBaseEventChart;
